import { useState } from "react";
import { Check, Link2, Plus, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  ImportDecision,
  ImportDecisionAnswer,
} from "@/lib/import/import-types";

/**
 * Une question de rapprochement.
 *
 * Trois principes :
 *
 *   - la **confiance est exprimee en mots**, pas en pourcentages. Le score
 *     numerique reste en infobulle : « 94 % » ne dit pas a un conservateur s'il
 *     doit valider ;
 *   - le **nombre de lignes concernees** est visible et inspectable. Rendre le
 *     rayon d'impact d'une decision explicite est ce qui transforme l'anxiete en
 *     confiance ;
 *   - creer une fiche affiche **le texte exact** qui sera cree, et laisse le
 *     corriger : la faute de frappe de l'utilisateur ne doit pas devenir un nom
 *     de fiche permanent.
 */
type Confidence = "certain" | "probable" | "doubtful" | "none";

function confidenceOf(decision: ImportDecision): Confidence {
  const top = decision.suggestions[0];
  if (!top) return "none";
  const presentationOnly = [
    "alias",
    "exact",
    "exactNormalized",
    "tokenSetExact",
    "initialsExpanded",
  ].includes(top.reason);
  if (presentationOnly) return "certain";
  if (top.score >= 0.9) return "probable";
  return "doubtful";
}

const ConfidenceBadge = ({ decision }: { decision: ImportDecision }) => {
  const vt = useVisibleT();
  const confidence = confidenceOf(decision);
  const score = decision.suggestions[0]?.score;

  const labels: Record<Confidence, string> = {
    certain: vt("bo.import.confidence.certain", "Certain"),
    probable: vt("bo.import.confidence.probable", "Probable"),
    doubtful: vt("bo.import.confidence.doubtful", "Incertain"),
    none: vt("bo.import.confidence.none", "Aucune"),
  };
  const classes: Record<Confidence, string> = {
    certain: "bg-green-100 text-green-800 hover:bg-green-100",
    probable: "bg-amber-100 text-amber-800 hover:bg-amber-100",
    doubtful: "bg-red-100 text-red-800 hover:bg-red-100",
    none: "bg-gray-100 text-gray-600 hover:bg-gray-100",
  };

  return (
    <Badge
      className={cn("font-normal", classes[confidence])}
      // Le chiffre reste accessible sans encombrer la lecture.
      title={score != null ? `${Math.round(score * 100)} %` : undefined}
    >
      {labels[confidence]}
    </Badge>
  );
};

export const ImportDecisionRow = ({
  decision,
  category,
  onAnswer,
  saving,
}: {
  decision: ImportDecision;
  /** libellé du champ/type de fiche liée (colonne « Catégorie ») */
  category?: string;
  onAnswer: (answer: ImportDecisionAnswer) => void;
  saving: boolean;
}) => {
  const vt = useVisibleT();
  const [createLabel, setCreateLabel] = useState(decision.rawValue);
  const [creating, setCreating] = useState(false);

  const top = decision.suggestions[0];
  const decided = decision.state === "resolved" || decision.state === "ignored";
  const isRelation = decision.kind === "relationValue";

  return (
    <TableRow className={cn(decided && "opacity-60")}>
      <TableCell className="align-top">
        <span className="text-sm text-gray-600">{category ?? "—"}</span>
      </TableCell>

      <TableCell className="align-top">
        <div className="flex flex-col gap-0.5">
          <span className="font-medium">{decision.rawValue}</span>
          {decision.occurrences > 1 ? (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0 justify-start text-xs text-gray-500"
                >
                  {decision.occurrences}{" "}
                  {vt("bo.import.decisions.affectedRows", "lignes concernées")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 text-sm">
                <p className="font-medium mb-1">
                  {vt("bo.import.decisions.rowsList", "Lignes du fichier")}
                </p>
                <p className="text-gray-600 break-words">
                  {decision.rowRefs.slice(0, 20).join(", ")}
                  {decision.rowRefs.length < decision.occurrences &&
                    ` … ${vt("bo.import.decisions.andOthers", "et d'autres")}`}
                </p>
              </PopoverContent>
            </Popover>
          ) : (
            <span className="text-xs text-gray-500">
              {vt("bo.import.decisions.oneRow", "1 ligne")}
            </span>
          )}
        </div>
      </TableCell>

      <TableCell className="align-top">
        {top ? (
          <div className="flex flex-col gap-0.5">
            <span className="flex items-center gap-1.5">
              {isRelation && (
                <Link2 className="w-3.5 h-3.5 text-blue-500" aria-hidden />
              )}
              {top.label}
            </span>
            {top.hint && (
              <span className="text-xs text-gray-500">{top.hint}</span>
            )}
          </div>
        ) : (
          <span className="text-sm text-gray-500">
            {vt("bo.import.decisions.noCandidate", "Aucune fiche correspondante")}
          </span>
        )}
      </TableCell>

      <TableCell className="align-top">
        <ConfidenceBadge decision={decision} />
      </TableCell>

      <TableCell className="align-top">
        {creating ? (
          <div className="flex flex-col gap-2">
            <span className="text-xs text-gray-600">
              {vt(
                "bo.import.decisions.createHint",
                "Nom à créer — corrigez-le si besoin :",
              )}
            </span>
            <Input
              value={createLabel}
              onChange={(event) => setCreateLabel(event.target.value)}
              className="h-8"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={saving || !createLabel.trim()}
                onClick={() => {
                  onAnswer({
                    id: decision.id,
                    action: "create",
                    value: createLabel.trim(),
                  });
                  setCreating(false);
                }}
              >
                {vt("bo.import.decisions.confirmCreate", "Créer")}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>
                {vt("bo.import.cancel", "Annuler")}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {top?.id != null && (
              <Button
                size="sm"
                variant={decided ? "outline" : "default"}
                disabled={saving}
                onClick={() =>
                  onAnswer(
                    isRelation
                      ? { id: decision.id, action: "link", targetId: top.id as string, remember: true }
                      : { id: decision.id, action: "value", value: top.label },
                  )
                }
              >
                <Check className="w-3.5 h-3.5 mr-1" aria-hidden />
                {vt("bo.import.decisions.link", "Lier")}
              </Button>
            )}

            {decision.suggestions.length > 1 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button size="sm" variant="outline" disabled={saving}>
                    {vt("bo.import.decisions.chooseOther", "Choisir…")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-2">
                  <div className="flex flex-col">
                    {decision.suggestions.map((suggestion) => (
                      <Button
                        key={`${suggestion.id}-${suggestion.label}`}
                        variant="ghost"
                        className="justify-between h-auto py-2"
                        onClick={() =>
                          onAnswer(
                            isRelation
                              ? {
                                  id: decision.id,
                                  action: "link",
                                  targetId: suggestion.id as string,
                                  remember: true,
                                }
                              : {
                                  id: decision.id,
                                  action: "value",
                                  value: suggestion.label,
                                },
                          )
                        }
                      >
                        <span className="text-left">
                          {suggestion.label}
                          {suggestion.hint && (
                            <span className="block text-xs text-gray-500">
                              {suggestion.hint}
                            </span>
                          )}
                        </span>
                        <span className="text-xs text-gray-400">
                          {Math.round(suggestion.score * 100)} %
                        </span>
                      </Button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}

            <Button
              size="sm"
              variant="outline"
              disabled={saving}
              onClick={() => setCreating(true)}
            >
              <Plus className="w-3.5 h-3.5 mr-1" aria-hidden />
              {vt("bo.import.decisions.create", "Créer")}
            </Button>

            <Button
              size="sm"
              variant="ghost"
              disabled={saving}
              onClick={() => onAnswer({ id: decision.id, action: "skip" })}
            >
              <X className="w-3.5 h-3.5 mr-1" aria-hidden />
              {vt("bo.import.decisions.leaveEmpty", "Laisser vide")}
            </Button>
          </div>
        )}
      </TableCell>

      <TableCell className="align-top">
        {decided ? (
          decision.resolution?.action === "create" ? (
            // Indicateur explicite : cette valeur ne correspond à aucune fiche,
            // une **nouvelle fiche liée sera créée** à l'import.
            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 font-normal">
              <Plus className="w-3 h-3 mr-1" aria-hidden />
              {vt("bo.import.decisions.stateWillCreate", "Sera créée")}
            </Badge>
          ) : (
            <Badge variant="secondary" className="font-normal">
              {decision.state === "ignored"
                ? vt("bo.import.decisions.stateIgnored", "Laissé vide")
                : vt("bo.import.decisions.stateDecided", "Décidé")}
            </Badge>
          )
        ) : decision.state === "auto" ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 font-normal">
            {vt("bo.import.decisions.stateAuto", "Reconnu")}
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 font-normal">
            {vt("bo.import.decisions.statePending", "À confirmer")}
          </Badge>
        )}
      </TableCell>
    </TableRow>
  );
};
